import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import apiClient from '../utils/api';
import { useAuth } from './AuthContext';

const AudioContext = createContext();

// LocalStorage keys
const STORAGE_KEYS = {
  ACTIVE_FREQUENCY_ID: 'zenta_active_frequency_id',
  AUDIO_ENABLED: 'zenta_audio_enabled',
  VOLUME: 'zenta_volume',
};

// Helper to safely get from localStorage
const getStoredValue = (key, defaultValue) => {
  try {
    const stored = localStorage.getItem(key);
    if (stored === null) return defaultValue;
    if (key === STORAGE_KEYS.VOLUME) return parseFloat(stored);
    if (key === STORAGE_KEYS.AUDIO_ENABLED) return stored === 'true';
    return stored;
  } catch (e) {
    console.warn('[AudioContext] LocalStorage error:', e);
    return defaultValue;
  }
};

// Helper to safely set to localStorage
const setStoredValue = (key, value) => {
  try {
    if (value === null || value === undefined) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, String(value));
    }
  } catch (e) {
    console.warn('[AudioContext] LocalStorage write error:', e);
  }
};

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within AudioProvider');
  }
  return context;
};

export const AudioProvider = ({ children }) => {
  const { user } = useAuth(); // Listen to user state
  const [activeFrequency, setActiveFrequencyState] = useState(null);
  const [audioEnabled, setAudioEnabledState] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolumeState] = useState(() => getStoredValue(STORAGE_KEYS.VOLUME, 0.7));
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [audioTracks, setAudioTracks] = useState([]);
  const [isRestoring, setIsRestoring] = useState(true); // Track if we're restoring state
  
  const audioRef = useRef(null);
  const audioTracksRef = useRef([]);
  const currentTrackIndexRef = useRef(0);
  const hasRestoredRef = useRef(false); // Prevent multiple restores

  // Wrapped setters that also persist to localStorage
  const setActiveFrequency = useCallback((freq) => {
    setActiveFrequencyState(freq);
    setStoredValue(STORAGE_KEYS.ACTIVE_FREQUENCY_ID, freq?.id || null);
  }, []);

  const setAudioEnabled = useCallback((enabled) => {
    setAudioEnabledState(enabled);
    setStoredValue(STORAGE_KEYS.AUDIO_ENABLED, enabled);
  }, []);

  const setVolume = useCallback((vol) => {
    setVolumeState(vol);
    setStoredValue(STORAGE_KEYS.VOLUME, vol);
  }, []);

  // Keep refs in sync with state
  useEffect(() => {
    audioTracksRef.current = audioTracks;
  }, [audioTracks]);

  useEffect(() => {
    currentTrackIndexRef.current = currentTrackIndex;
  }, [currentTrackIndex]);

  // Restore audio state from localStorage when user logs in
  useEffect(() => {
    const restoreAudioState = async () => {
      if (!user || hasRestoredRef.current) {
        setIsRestoring(false);
        return;
      }

      hasRestoredRef.current = true;
      console.log('[AudioContext] Restoring audio state for user');

      try {
        const storedFreqId = getStoredValue(STORAGE_KEYS.ACTIVE_FREQUENCY_ID, null);
        const storedAudioEnabled = getStoredValue(STORAGE_KEYS.AUDIO_ENABLED, false);

        if (storedFreqId && storedAudioEnabled) {
          console.log('[AudioContext] Found stored frequency:', storedFreqId);
          
          // Fetch all frequencies to find the stored one
          const frequencies = await apiClient.getFrequencies();
          const frequencyArray = Array.isArray(frequencies) ? frequencies : (frequencies?.data || []);
          const storedFreq = frequencyArray.find(f => f.id === storedFreqId);

          if (storedFreq) {
            console.log('[AudioContext] Restoring frequency:', storedFreq.name);
            setActiveFrequencyState(storedFreq);
            // Small delay to let the frequency tracks load
            setTimeout(() => {
              setAudioEnabledState(true);
            }, 500);
          } else {
            console.log('[AudioContext] Stored frequency not found, clearing');
            setStoredValue(STORAGE_KEYS.ACTIVE_FREQUENCY_ID, null);
            setStoredValue(STORAGE_KEYS.AUDIO_ENABLED, false);
          }
        }
      } catch (error) {
        console.warn('[AudioContext] Error restoring audio state:', error);
      } finally {
        setIsRestoring(false);
      }
    };

    restoreAudioState();
  }, [user]);

  // Clear audio when user logs out
  useEffect(() => {
    if (!user) {
      console.log('[AudioContext] User logged out, clearing audio');
      hasRestoredRef.current = false; // Reset for next login
      // Stop and clear audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      // Reset all states but keep localStorage for next login
      setActiveFrequencyState(null);
      setAudioEnabledState(false);
      setIsPlaying(false);
      setAudioTracks([]);
      setCurrentTrackIndex(0);
    }
  }, [user]);

  // Stop audio when no frequency is active (but not during restore)
  useEffect(() => {
    if (isRestoring) return; // Don't clear during restoration
    
    if (!activeFrequency) {
      console.log('[AudioContext] No active frequency, stopping audio');
      // Stop audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      // Disable audio and reset states
      setAudioEnabledState(false);
      setStoredValue(STORAGE_KEYS.AUDIO_ENABLED, false);
      setIsPlaying(false);
      setAudioTracks([]);
      setCurrentTrackIndex(0);
    }
  }, [activeFrequency, isRestoring]);

  // Initialize audio element
  useEffect(() => {
    const audio = new Audio();
    audio.volume = volume;
    audioRef.current = audio;

    // Event listeners
    const handleEnded = () => {
      console.log('[AudioContext] Track ended, auto-playing next track');
      // Auto-play next track - use refs to get current values
      const tracks = audioTracksRef.current;
      const currentIdx = currentTrackIndexRef.current;
      
      if (tracks.length === 0) return;
      
      let nextIndex;
      if (tracks.length === 1) {
        nextIndex = 0;
      } else {
        // Get random index different from current
        do {
          nextIndex = Math.floor(Math.random() * tracks.length);
        } while (nextIndex === currentIdx);
      }
      
      console.log(`[AudioContext] Moving to track ${nextIndex + 1} of ${tracks.length}`);
      setCurrentTrackIndex(nextIndex);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    // CRITICAL: Detect when audio stops unexpectedly (connection lost, app closed, etc.)
    const handleError = () => {
      console.log('Audio error detected - disabling audio');
      setAudioEnabledState(false);
      setStoredValue(STORAGE_KEYS.AUDIO_ENABLED, false);
      setIsPlaying(false);
    };

    const handleStalled = () => {
      console.log('Audio stalled - checking status');
      // If audio stalls and can't recover, disable
      setTimeout(() => {
        if (audioRef.current && audioRef.current.networkState === audioRef.current.NETWORK_NO_SOURCE) {
          console.log('No audio source - disabling audio');
          setAudioEnabledState(false);
          setStoredValue(STORAGE_KEYS.AUDIO_ENABLED, false);
          setIsPlaying(false);
        }
      }, 3000);
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('error', handleError);
    audio.addEventListener('stalled', handleStalled);

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('stalled', handleStalled);
      audio.pause();
      audio.src = '';
    };
  }, []);

  // Update audio tracks when frequency changes
  useEffect(() => {
    console.log('[AudioContext] Active frequency changed:', activeFrequency);
    if (activeFrequency?.audio_files && activeFrequency.audio_files.length > 0) {
      console.log('[AudioContext] Setting audio tracks:', activeFrequency.audio_files.length);
      setAudioTracks(activeFrequency.audio_files);
      // Random track on frequency change
      const randomIndex = Math.floor(Math.random() * activeFrequency.audio_files.length);
      setCurrentTrackIndex(randomIndex);
    } else {
      console.log('[AudioContext] No audio files for frequency');
      setAudioTracks([]);
    }
  }, [activeFrequency]);

  // Load and play track when it changes
  useEffect(() => {
    if (!audioRef.current || !audioEnabled || audioTracks.length === 0) return;

    const currentTrackUrl = apiClient.getAudioUrl(audioTracks[currentTrackIndex]);
    audioRef.current.src = currentTrackUrl;
    audioRef.current.load();
    
    const playPromise = audioRef.current.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          setIsPlaying(true);
        })
        .catch((error) => {
          console.log('Auto-play prevented:', error);
          setIsPlaying(false);
        });
    }
  }, [currentTrackIndex, audioTracks, audioEnabled]);

  // Control playback based on audioEnabled
  useEffect(() => {
    if (!audioRef.current || audioTracks.length === 0) return;

    if (audioEnabled) {
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
          })
          .catch((error) => {
            console.log('Auto-play prevented by browser:', error);
            setIsPlaying(false);
          });
      }
    } else {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, [audioEnabled, audioTracks]);

  // Update volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const toggleAudio = () => {
    console.log('[AudioContext] Toggle audio:', !audioEnabled, 'Active freq:', activeFrequency?.name);
    setAudioEnabled(!audioEnabled);
  };

  const togglePlayPause = () => {
    if (!audioRef.current || !audioEnabled) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch((error) => {
        console.error('Error playing audio:', error);
      });
    }
  };

  const handleNext = () => {
    const tracks = audioTracksRef.current;
    const currentIdx = currentTrackIndexRef.current;
    
    if (tracks.length === 0) return;
    
    let nextIndex;
    if (tracks.length === 1) {
      nextIndex = 0;
    } else {
      do {
        nextIndex = Math.floor(Math.random() * tracks.length);
      } while (nextIndex === currentIdx);
    }
    
    setCurrentTrackIndex(nextIndex);
  };

  const handlePrevious = () => {
    const tracks = audioTracksRef.current;
    const currentIdx = currentTrackIndexRef.current;
    
    if (tracks.length === 0) return;
    
    let prevIndex;
    if (tracks.length === 1) {
      prevIndex = 0;
    } else {
      do {
        prevIndex = Math.floor(Math.random() * tracks.length);
      } while (prevIndex === currentIdx);
    }
    
    setCurrentTrackIndex(prevIndex);
  };

  const clearAudio = () => {
    console.log('[AudioContext] Clearing all audio (logout)');
    // Stop and clear audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    // Reset all states and clear localStorage
    setActiveFrequencyState(null);
    setAudioEnabledState(false);
    setIsPlaying(false);
    setAudioTracks([]);
    setCurrentTrackIndex(0);
    // Clear localStorage
    setStoredValue(STORAGE_KEYS.ACTIVE_FREQUENCY_ID, null);
    setStoredValue(STORAGE_KEYS.AUDIO_ENABLED, false);
    hasRestoredRef.current = false;
  };

  const value = {
    activeFrequency,
    setActiveFrequency,
    audioEnabled,
    toggleAudio,
    isPlaying,
    volume,
    setVolume,
    currentTrackIndex,
    audioTracks,
    togglePlayPause,
    handleNext,
    handlePrevious,
    clearAudio,
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};
